import { dec, type Dec } from './dec'
import { formulaDaJson } from './formula-json'
import type {
  ContatorePeriodo,
  DatiPeriodo,
  Elenchi,
  FormulePersonalizzate,
  LetturaPeriodo,
  OverrideKwh,
  QuadroSIngresso,
} from './ingresso'

/** Confine col JSON di dichiarazione_completa: valori ancora stringhe, null dal database. */
type Grezzo = Record<string, unknown>

function testoOpzionale(v: unknown): string | undefined {
  return v === undefined || v === null ? undefined : String(v)
}

function decOpzionale(v: unknown): Dec | undefined {
  return v === undefined || v === null ? undefined : dec(v as string)
}

function perMese(v: unknown): Readonly<Record<number, Dec>> {
  const voci = Object.entries((v as Record<string, string> | undefined) ?? {})
  return Object.fromEntries(voci.map(([mese, valore]) => [Number(mese), dec(valore)]))
}

function contatoreDaJson(c: Grezzo): ContatorePeriodo {
  return {
    id: String(c.id),
    codice: c.codice as ContatorePeriodo['codice'],
    matricola: String(c.matricola),
    cifreIntere: Number(c.cifreIntere ?? 0),
    costanteLettura: dec((c.costanteLettura as string | undefined) ?? '1'),
    kwhDiretti: c.kwhDiretti === true,
    tipologiaConsumo: testoOpzionale(c.tipologiaConsumo),
    tipoScambio: testoOpzionale(c.tipoScambio),
    idControparte: testoOpzionale(c.idControparte),
    pod: (c.pod as string[] | undefined) ?? [],
  }
}

function formuleDaJson(fp: Grezzo): FormulePersonalizzate {
  const perDifferenza = fp.perDifferenza as Grezzo | undefined
  return {
    righi: ((fp.righi as Grezzo[] | undefined) ?? []).map((r) => ({
      quadro: r.quadro as 'J' | 'L' | 'M',
      progr: Number(r.progr),
      numeroUtenze: Number(r.numeroUtenze),
      formula: formulaDaJson(r.formula as Grezzo),
    })),
    quadroM: fp.quadroM as FormulePersonalizzate['quadroM'],
    perDifferenza:
      perDifferenza === undefined
        ? undefined
        : {
            quadro: perDifferenza.quadro as 'C' | 'E',
            tipologia: String(perDifferenza.tipologia),
            formula: formulaDaJson(perDifferenza.formula as Grezzo),
          },
    meseInizioAttivita: fp.meseInizioAttivita as number | undefined,
  }
}

export function datiPeriodoDaJson(raw: Grezzo): DatiPeriodo {
  const letture = ((raw.letture as Grezzo[] | undefined) ?? []).map(
    (l): LetturaPeriodo => ({
      contatoreId: String(l.contatoreId),
      anno: Number(l.anno),
      mese: Number(l.mese),
      valore: dec(l.valore as string),
    }),
  )

  const qs = (raw.quadroS as Grezzo | undefined) ?? {}
  const quadroS: QuadroSIngresso = {
    acconto: decOpzionale(qs.acconto),
    creditoInizio: decOpzionale(qs.creditoInizio),
    debitoInizio: decOpzionale(qs.debitoInizio),
    creditoUtilizzato: perMese(qs.creditoUtilizzato),
    versatoOverride: perMese(qs.versatoOverride),
  }

  const ov = (raw.override as Record<string, Record<string, unknown>> | undefined) ?? {}
  const override: OverrideKwh = Object.fromEntries(
    Object.entries(ov).map(([quadro, perChiave]) => [
      quadro,
      Object.fromEntries(Object.entries(perChiave).map(([chiave, mesi]) => [chiave, perMese(mesi)])),
    ]),
  )

  const el = (raw.elenchi as Grezzo | undefined) ?? {}
  const elenchi: Elenchi = {
    N: ((el.N as Grezzo[] | undefined) ?? []).map((n) => ({
      tipologia: Number(n.tipologia),
      id: String(n.id),
      kwh: dec(n.kwh as string),
    })),
    T: ((el.T as Grezzo[] | undefined) ?? []).map((t) => ({
      provenienza: Number(t.provenienza),
      id: String(t.id),
      kwh: dec(t.kwh as string),
    })),
    perdite: decOpzionale(el.perdite),
  }

  return {
    tipologia: raw.tipologia as DatiPeriodo['tipologia'],
    tipologiaDichiarante: raw.tipologiaDichiarante as DatiPeriodo['tipologiaDichiarante'],
    periodo: raw.periodo as DatiPeriodo['periodo'],
    annoImposta: Number(raw.annoImposta),
    provincia: String(raw.provincia ?? ''),
    codiceCatastale: testoOpzionale(raw.codiceCatastale),
    contatori: ((raw.contatori as Grezzo[] | undefined) ?? []).map(contatoreDaJson),
    letture,
    formulePersonalizzate: formuleDaJson((raw.formulePersonalizzate as Grezzo | undefined) ?? {}),
    quadroS,
    override,
    elenchi,
    vendite: ((raw.vendite as Grezzo[] | undefined) ?? []).map((v) => ({
      mese: Number(v.mese),
      provincia: String(v.provincia),
      tipologia: String(v.tipologia),
      numeroUtenze: Number(v.numeroUtenze),
      kwh: dec(v.kwh as string),
    })),
  }
}
